import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, TrendingUp, Users, Package, Snowflake, Plus, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ publicaciones: 0, enviadas: 0, ofertas: 0, compradores: 0, frigorificos: 0 });
  const [recentPubs, setRecentPubs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [pubRes, compRes, frigoRes] = await Promise.all([
        axios.get(`${API}/publicaciones/paginated`, { params: { limit: 5, sortField: 'createdAt', sortDirection: 'desc' } }),
        axios.get(`${API}/compradores`),
        axios.get(`${API}/frigorificos`),
      ]);

      const pubs = pubRes.data.items || [];
      const totalPubs = pubRes.data.total || 0;
      const enviadas = pubs.filter(p => p.estadoPublicacion === 'Enviada').length;
      const totalOfertas = pubs.reduce((sum, p) => sum + (p.ofertasCount || 0), 0);

      setStats({
        publicaciones: totalPubs,
        enviadas,
        ofertas: totalOfertas,
        compradores: compRes.data.length,
        frigorificos: frigoRes.data.length,
      });
      setRecentPubs(pubs);
    } catch {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const estadoBadge = (estado) => {
    const map = {
      'Borrador': 'bg-slate-500/15 text-slate-600 dark:text-slate-400',
      'Enviada': 'bg-sky-500/15 text-sky-700 dark:text-sky-400',
      'Adjudicada': 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
      'En espera': 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
      'No adjudicada': 'bg-rose-500/15 text-rose-700 dark:text-rose-400',
    };
    return map[estado] || '';
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]"><div className="text-muted-foreground">Cargando...</div></div>;
  }

  const indicators = [
    { label: 'Ofertas', value: stats.publicaciones, icon: FileText, color: 'text-blue-500', bg: 'bg-blue-500/10' },
    { label: 'Enviadas', value: stats.enviadas, icon: Package, color: 'text-sky-500', bg: 'bg-sky-500/10' },
    { label: 'Bids', value: stats.ofertas, icon: TrendingUp, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
    { label: 'Compradores', value: stats.compradores, icon: Users, color: 'text-purple-500', bg: 'bg-purple-500/10' },
    { label: 'Frigoríficos', value: stats.frigorificos, icon: Snowflake, color: 'text-cyan-500', bg: 'bg-cyan-500/10' },
  ];

  return (
    <div className="space-y-6 bg-background min-h-screen">
      <div className="bg-background border-b">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-semibold tracking-tight">Dashboard</h1>
              <p className="text-muted-foreground mt-1">Resumen de actividad</p>
            </div>
            <Button onClick={() => navigate('/publicaciones/nueva')}>
              <Plus className="h-4 w-4 mr-2" /> Nueva Oferta
            </Button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 space-y-6">
        {/* Indicator Cards */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {indicators.map((ind) => {
            const Icon = ind.icon;
            return (
              <Card key={ind.label} className="p-5">
                <div className="flex items-center gap-3">
                  <div className={`p-2.5 rounded-lg ${ind.bg}`}><Icon className={`h-5 w-5 ${ind.color}`} /></div>
                  <div>
                    <p className="text-2xl font-bold">{ind.value}</p>
                    <p className="text-xs text-muted-foreground">{ind.label}</p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Recent Publications */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Ofertas Recientes</h2>
            <Button variant="ghost" size="sm" onClick={() => navigate('/publicaciones')}>
              Ver todas <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
          <div className="space-y-3">
            {recentPubs.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No hay ofertas aún</p>
            ) : (
              recentPubs.map(pub => (
                <div
                  key={pub.id}
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-accent/50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/publicaciones/${pub.id}`)}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="p-2 bg-primary/10 rounded-lg shrink-0"><Snowflake className="h-4 w-4 text-primary" /></div>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{pub.frigorificoNombre}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Badge variant="outline" className="text-[10px] px-1.5">{pub.tipo}</Badge>
                        #{pub.plantaNumero}
                        <span>{pub.fcls?.length || 0} FCLs</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {pub.ofertasCount > 0 && <Badge variant="secondary" className="text-xs">{pub.ofertasCount} bids</Badge>}
                    <Badge className={`text-xs ${estadoBadge(pub.estadoPublicacion)}`}>{pub.estadoPublicacion}</Badge>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;